import type { Dispatch, ReactNode, SetStateAction } from "react";
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

const DEFAULT_LOG_SERVICE = "/frontend-api";
const DEFAULT_FRONT_VERSION = "NOT_DEFINED";
const noop = () => {};

export const SESSION_EXPIRED_ERRORCODE = "SESSION_EXPIRED";

type AppStatus = "HEALTHY" | "BROKEN_FLOW" | "SESSION_EXPIRED";

export const APP_STATUS: Record<string, AppStatus> = {
  HEALTHY: "HEALTHY",
  BROKEN_FLOW: "BROKEN_FLOW",
  SESSION_EXPIRED: "SESSION_EXPIRED",
};

const AppStatusContext = createContext<AppStatus>(APP_STATUS.HEALTHY);

const AppSetStatusContext =
  createContext<Dispatch<SetStateAction<AppStatus>>>(noop);

export const AppLogServiceContext = createContext(DEFAULT_LOG_SERVICE);
export const AppFrontVersionContext = createContext(DEFAULT_FRONT_VERSION);

type InvalidateQueries = (...args: Array<unknown>) => unknown;

const OnRestartAppStatusContext = createContext<InvalidateQueries>(noop);

interface LegacyAppErrorProviderProps {
  invalidateQueries?: InvalidateQueries;
}

type AppErrorProviderProps = {
  logService?: string;
  children: ReactNode;
  frontVersion?: string;
} & (
  | {
      onRestartAppStatus?: () => void;
    }
  | LegacyAppErrorProviderProps
);

export default function AppErrorProvider(props: AppErrorProviderProps) {
  const {
    logService = DEFAULT_LOG_SERVICE,
    children,
    frontVersion = DEFAULT_FRONT_VERSION,
  } = props;
  let onRestartAppStatus =
    "onRestartAppStatus" in props ? props?.onRestartAppStatus : undefined;
  const invalidateQueries =
    "invalidateQueries" in props ? props?.invalidateQueries : undefined;
  if (invalidateQueries) {
    onRestartAppStatus = () => {
      invalidateQueries();
    };
  }
  const contextInvalidateQueries = useContext(OnRestartAppStatusContext);
  const contextFrontVersion = useContext(AppFrontVersionContext);
  const frontVersionValue = frontVersion || contextFrontVersion;
  const [appStatus, setAppStatus] = useState(APP_STATUS.HEALTHY);

  return (
    <AppLogServiceContext.Provider value={logService}>
      <AppFrontVersionContext.Provider value={frontVersionValue}>
        <OnRestartAppStatusContext.Provider
          value={onRestartAppStatus || contextInvalidateQueries}
        >
          <AppSetStatusContext.Provider value={setAppStatus}>
            <AppStatusContext.Provider value={appStatus}>
              {children}
            </AppStatusContext.Provider>
          </AppSetStatusContext.Provider>
        </OnRestartAppStatusContext.Provider>
      </AppFrontVersionContext.Provider>
    </AppLogServiceContext.Provider>
  );
}

export const useShowBrokenFlow = () => {
  const status = useContext(AppStatusContext);
  return status === APP_STATUS.BROKEN_FLOW;
};

export const useShowSessionExpired = () => {
  const status = useContext(AppStatusContext);
  return status === APP_STATUS.SESSION_EXPIRED;
};

const createUseModal = (statusType: AppStatus) => () => {
  const onRestartAppStatus = useContext(OnRestartAppStatusContext);
  const status = useContext(AppStatusContext);
  const setAppStatus = useContext(AppSetStatusContext);
  return useMemo(
    () => ({
      isOpen: status === statusType,
      close: () => {
        onRestartAppStatus();
        setAppStatus(APP_STATUS.HEALTHY);
      },
    }),
    [status, setAppStatus, onRestartAppStatus],
  );
};

export const useBrokenFlowModal = createUseModal(APP_STATUS.BROKEN_FLOW);

export const useSessionExpiredModal = createUseModal(
  APP_STATUS.SESSION_EXPIRED,
);

export const useLaunchBrokenFlow = () => {
  const setAppStatus = useContext(AppSetStatusContext);
  return useCallback(() => {
    setAppStatus(APP_STATUS.BROKEN_FLOW);
  }, [setAppStatus]);
};

export const useLaunchSessionExpired = () => {
  const setAppStatus = useContext(AppSetStatusContext);
  return useCallback(() => {
    setAppStatus((status) =>
      status === APP_STATUS.HEALTHY ? APP_STATUS.SESSION_EXPIRED : status,
    );
  }, [setAppStatus]);
};
